import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabaseChatService, ChatMessage } from '../services/supabaseChatService';
import {
  type AdminChatUser,
  applyMessageEventToAdminChatUsers,
} from './adminChatUsersHelpers';

export const useAdminMessages = (userId: string, otherUserId: string) => {
  const queryClient = useQueryClient();

  const { data, ...rest } = useQuery({
    queryKey: ['admin-chat-messages', userId, otherUserId],
    queryFn: () => supabaseChatService.getMessages(userId, otherUserId),
    enabled: !!userId && !!otherUserId,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!userId) return;
    const unsubscribe = supabaseChatService.subscribeToMessages(userId, (msg) => {
      queryClient.setQueriesData<AdminChatUser[]>(
        {
          queryKey: ['admin-chat-users', userId],
          exact: false,
        },
        (currentUsers) => {
          if (!Array.isArray(currentUsers)) {
            return currentUsers;
          }

          return applyMessageEventToAdminChatUsers(currentUsers, userId, msg);
        }
      );

      queryClient.invalidateQueries({
        queryKey: ['admin-chat-users', userId],
        exact: false,
      });

      if (
        (msg.sender_id === userId && msg.receiver_id === otherUserId) ||
        (msg.sender_id === otherUserId && msg.receiver_id === userId)
      ) {
        queryClient.invalidateQueries({
          queryKey: ['admin-chat-messages', userId, otherUserId],
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [userId, otherUserId, queryClient]);

  return { data: data ?? ([] as ChatMessage[]), ...rest };
};
